<!--
  What the account menu holds — who is signed in, the notifications the layout provides, sign out,
  the quick actions, theme, language, invitations, account settings, the organizations, help and
  version — whichever container shows it: `UserDrawer` beside the rail, `MoreSheet` on a phone
  (docs/plans/mobile-navigation.md MN3). The `before` slot sits above the header and the default
  slot under it, for what only the container has. `close` asks the container to shut once
  something in the menu was opened.
-->
<template>
  <div class="user-menu">
    <div class="user-menu__content">
      <slot name="before" />

      <!-- Profile Header -->
      <div class="user-menu__header">
        <div class="user-menu__avatar-wrapper" @click="handleAvatarClick">
          <v-avatar v-if="userAvatar" size="44" class="user-menu__avatar">
            <v-img :src="userAvatar" cover />
          </v-avatar>
          <Avatar v-else :email="userEmail" :name="userDisplayName" :size="44" />
        </div>

        <div class="user-menu__user-info">
          <span class="user-menu__name">{{ userDisplayName }}</span>
          <span class="user-menu__email">{{ userEmail }}</span>
        </div>

        <div class="user-menu__header-actions">
          <!-- The caller's notifications, which the layout provides: `close` shuts the container
               once something in it was opened. -->
          <slot name="notifications" :close="close" />
          <v-tooltip :text="t('core.common.logout')" location="bottom">
            <template #activator="{ props: tooltip }">
              <v-btn
                v-bind="tooltip"
                icon
                variant="text"
                size="small"
                :aria-label="t('core.common.logout')"
                @click="handleSignOut"
              >
                <v-icon :icon="mdiLogout" size="18" />
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </div>

      <slot />

      <!-- Quick Actions -->
      <div v-if="shortcuts && shortcuts.length > 0" class="drawer-section">
        <div class="drawer-section__header">
          <span class="drawer-section__title">{{ t('core.userDrawer.quickActions') }}</span>
        </div>
        <div class="quick-actions__grid">
          <div
            v-for="action in shortcuts"
            :key="action.id"
            class="quick-actions__item"
            role="button"
            tabindex="0"
            @click="handleActionClick(action)"
            @keydown.enter="handleActionClick(action)"
          >
            <div class="quick-actions__icon">
              <v-badge
                v-if="action.badge"
                :content="action.badge"
                :color="action.badgeColor || 'error'"
                offset-x="2"
                offset-y="2"
              >
                <v-icon :icon="action.icon" size="22" />
              </v-badge>
              <v-icon v-else :icon="action.icon" size="22" />
            </div>
            <span class="quick-actions__label">{{ action.label }}</span>
          </div>
        </div>
      </div>

      <!-- Settings -->
      <div class="drawer-section">
        <div class="drawer-section__header">
          <span class="drawer-section__title">{{ t('core.userDrawer.settings.title') }}</span>
        </div>
        <div class="drawer-section__content">
          <ThemeToggle />
          <v-list density="compact" nav class="user-menu__settings-list">
            <LanguageMenu />
            <v-list-item
              v-if="organizationId && can('auth.members.invite')"
              slim
              @click="handleInvite"
            >
              <template #prepend>
                <v-icon :icon="mdiAccountPlus" size="20" />
              </template>
              <v-list-item-title>{{ t('core.userDrawer.settings.inviteUsers') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-for="setting in allSettingsLinks"
              :key="setting.text"
              slim
              :to="{ name: setting.route, params: setting.params }"
              @click="close"
            >
              <template #prepend>
                <v-icon :icon="setting.icon" size="20" />
              </template>
              <v-list-item-title>{{ setting.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </div>
      </div>

      <!-- Organizations -->
      <div v-if="organizations.length > 0" class="drawer-section">
        <div class="drawer-section__header">
          <span class="drawer-section__title">{{ t('core.userDrawer.organizations') }}</span>
          <span class="drawer-section__badge">{{ organizations.length }}</span>
        </div>
        <!-- Founding is offered wherever the platform lets its accounts found organizations, and
             greyed with the reason once this account owns as many as it allows. The layout opens
             the founding dialog. -->
        <OrganizationsList
          :show-title="false"
          :organizations="organizations"
          :can-create="founding !== 'closed'"
          :create-disabled-reason="foundingHint"
          @create-organization="handleCreateOrganization"
          @close-menu="close"
        />
      </div>
    </div>

    <!-- Footer -->
    <div class="user-menu__footer">
      <a href="#" class="user-menu__footer-link" @click.prevent="handleHelp">
        {{ t('core.userDrawer.helpLink') }}
      </a>
      <template v-if="appVersion">
        <span class="user-menu__footer-dot" />
        <a
          href="#"
          class="user-menu__footer-link user-menu__footer-link--version"
          @click.prevent="handleChangelog"
        >
          v{{ appVersion }}
        </a>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiLogout, mdiCog, mdiAccountPlus } from '@mdi/js'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useSessionStore } from '@/apps/session/stores/session'
import { OrganizationsList } from '../../../organizations'
import { useCan } from '../../../organizations/composables'
import LanguageMenu from '../ui/LanguageMenu.vue'
import ThemeToggle from '../ui/ThemeToggle.vue'
import Avatar from '../user/Avatar.vue'

interface QuickAction {
  id: string
  icon: string
  label: string
  tooltip: string
  route?: string
  action?: string
  category?: string
  badge?: number | string
  badgeColor?: string
}

interface SettingLink {
  icon: string
  text: string
  route: string
  params?: Record<string, string>
}

interface Props {
  userAvatar?: string
  userDisplayName?: string
  userEmail?: string
  shortcuts?: QuickAction[]
  settingsLinks?: SettingLink[]
  appVersion?: string
}

const props = withDefaults(defineProps<Props>(), {
  userDisplayName: 'User',
  userEmail: 'user@example.com',
})

const emit = defineEmits<{
  // Shut the container: something in the menu was opened.
  'close': []
  'sign-out': []
  'avatar-click': []
  'external-action': [action: QuickAction]
  'help': []
  'changelog': []
  // Invite people into the organization the URL names: the layout opens the invitation dialog.
  'invite': []
  // Found another organization: the layout opens the founding dialog.
  'create-organization': []
}>()

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const session = useSessionStore()
const { can } = useCan()

const organizations = computed(() => session.access?.memberships ?? [])
/** Where the caller stands against the platform's founding rule, as `/access` read it. */
const founding = computed(() => session.access?.founding.status ?? 'closed')
const foundingHint = computed(() => {
  switch (founding.value) {
    case 'limit_reached':
      return t('core.userDrawer.foundingLimitReached', { limit: session.access?.founding.limit ?? 0 })
    case 'unverified':
      return t('core.userDrawer.foundingUnverified')
    default:
      return undefined
  }
})

const organizationId = computed(() => route.params.organizationId as string)

const allSettingsLinks = computed<SettingLink[]>(() => {
  const accountText = t('core.userDrawer.settings.account')
  const text = accountText !== 'core.userDrawer.settings.account' ? accountText : 'Account Settings'
  const defaultLink: SettingLink = {
    icon: mdiCog,
    text,
    route: 'settings:profile',
    params: { organizationId: organizationId.value },
  }
  return [defaultLink, ...(props.settingsLinks || [])]
})

function close() {
  emit('close')
}

function handleActionClick(action: QuickAction) {
  if (action.route) {
    close()
    router.push({ name: action.route })
  } else {
    emit('external-action', action)
  }
}

const handleSignOut = () => {
  close()
  emit('sign-out')
}

const handleAvatarClick = () => {
  emit('avatar-click')
}

const handleHelp = () => {
  emit('help')
}

const handleChangelog = () => {
  emit('changelog')
}

function handleInvite() {
  close()
  emit('invite')
}

function handleCreateOrganization() {
  close()
  emit('create-organization')
}
</script>

<style lang="scss">
@use '@/styles/design-tokens' as *;

// ---------------------------------------------------------------------------
// User Menu
// ---------------------------------------------------------------------------
.user-menu {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;

  // Scrollable body
  &__content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: rgba(var(--v-theme-on-surface), 0.08) transparent;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(var(--v-theme-on-surface), 0.08);
      border-radius: $radius-full;
    }
  }

  // -------------------------------------------------------------------------
  // Profile Header
  // -------------------------------------------------------------------------
  &__header {
    display: flex;
    align-items: center;
    gap: $space-md;
    padding: $space-lg;
    border-bottom: 1px solid rgba(var(--v-border-color), $opacity-border);
  }

  &__avatar-wrapper {
    flex-shrink: 0;
    cursor: pointer;
    @include transition-fast(transform);

    &:hover {
      transform: scale(1.05);
    }
  }

  &__user-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__name {
    font-size: $text-md;
    font-weight: $font-semibold;
    line-height: $leading-tight;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__email {
    font-size: $text-sm;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__header-actions {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 2px;

    .v-btn {
      color: rgba(var(--v-theme-on-surface), $opacity-secondary);
      @include transition-fast;

      &:hover {
        color: rgba(var(--v-theme-on-surface), $opacity-primary);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Settings list
  // -------------------------------------------------------------------------
  &__settings-list {
    padding: 0 !important;

    .v-list-item {
      border-radius: $radius-md;
      margin: 2px $space-xs;
      min-height: 40px;

      &:hover {
        background: rgba(var(--v-theme-on-surface), 0.04);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Footer
  // -------------------------------------------------------------------------
  &__footer {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $space-sm;
    padding: $space-md $space-lg;
    border-top: 1px solid rgba(var(--v-border-color), $opacity-border);
    background: rgba(var(--v-theme-surface), 0.95);
    backdrop-filter: blur(10px);
  }

  &__footer-link {
    font-size: $text-sm;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
    text-decoration: none;
    @include transition-fast;

    &:hover {
      color: rgb(var(--v-theme-primary));
    }

    &--version {
      font-size: $text-xs;
      background: rgba(var(--v-theme-on-surface), 0.06);
      padding: 2px $space-sm;
      border-radius: $radius-full;
    }
  }

  &__footer-dot {
    width: 3px;
    height: 3px;
    border-radius: $radius-full;
    background: rgba(var(--v-theme-on-surface), $opacity-tertiary);
    flex-shrink: 0;
  }
}

// ---------------------------------------------------------------------------
// Drawer Sections
// ---------------------------------------------------------------------------
.drawer-section {
  padding: $space-md 0;

  & + & {
    border-top: 1px solid rgba(var(--v-border-color), $opacity-border);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 $space-lg;
    margin-bottom: $space-sm;
  }

  &__title {
    font-size: $text-xs;
    font-weight: $font-semibold;
    text-transform: uppercase;
    letter-spacing: $tracking-wider;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary);
  }

  &__badge {
    font-size: $text-xs;
    font-weight: $font-medium;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
    background: rgba(var(--v-theme-on-surface), 0.06);
    padding: 1px $space-sm;
    border-radius: $radius-full;
    min-width: 20px;
    text-align: center;
  }

  &__content {
    padding: 0 $space-sm;
  }
}

// ---------------------------------------------------------------------------
// Quick Actions
// ---------------------------------------------------------------------------
.quick-actions {
  &__grid {
    display: flex;
    flex-wrap: wrap;
    gap: $space-sm;
    padding: 0 $space-lg;
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-xs;
    width: 76px;
    padding: $space-sm $space-xs;
    border-radius: $radius-lg;
    cursor: pointer;
    border: none;
    background: rgba(var(--v-theme-on-surface), 0.03);
    outline: none;
    @include transition-fast;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.06);
    }

    &:active {
      transform: scale(0.97);
    }

    @include focus-visible-only;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: $radius-lg;
    background: rgba(var(--v-theme-primary), 0.08);
    color: rgb(var(--v-theme-primary));
    @include transition-fast;

    .quick-actions__item:hover & {
      background: rgba(var(--v-theme-primary), 0.12);
    }
  }

  &__label {
    font-size: $text-xs;
    font-weight: $font-medium;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
    text-align: center;
    line-height: $leading-tight;
  }
}
</style>
